import React, { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useForgetPasswordConfirmMutation,
  useForgetPasswordRequestMutation,
} from "../../app/features/auth/authApi";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { useNavigate, Link } from "react-router-dom";
import EmailVerify from "../../components/PopUp/EmailVerify";
import RateLimitModal from "../../components/PopUp/RateLimitModal";
import { rateLimitMessage } from "../../utils/forms/parseApiError";

const ForgetPasswordConfirm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token");
  const navigate = useNavigate();
  const [forgetPasswordConfirm] = useForgetPasswordConfirmMutation();
  // console.log(token);

  const [showEmailVerifyNotification, setShowEmailVerifyNotification] =
    useState(false);
  const [rateLimit, setRateLimit] = useState({ open: false, message: "" });
  const handleEmailNotifiedClose = () => {
    setShowEmailVerifyNotification(false);
    navigate("/sign-in");
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

const onSubmit = async (data) => {
  const toastId = toast.loading("Processing your request. Please wait...");

  // ✅ Password length check
  if (data.password.length < 8) {
    toast.error("Password must be at least 8 characters long.", {
      id: toastId,
      duration: 4000,
    });
    return;
  }

  // ✅ Password match check
  if (data.password !== data.confirmPassword) {
    toast.error("Password and Confirm password must be the same", {
      id: toastId,
      duration: 4000,
    });
    return;
  }

  // ✅ Token check
  if (!token) {
    toast.error("Unauthorized Access", {
      id: toastId,
      duration: 4000,
    });
    navigate("/");
    return;
  }

  try {
    const res = await forgetPasswordConfirm({
      password: data.password,
      token,
    }).unwrap();

    if (res.message) {
      toast.success(res.message, {
        id: toastId,
        duration: 4000,
      });
      setShowEmailVerifyNotification(!showEmailVerifyNotification);
      reset();
    }
  } catch (error) {
    // ✅ Enhanced error handling for new backend responses
    if (error?.status === 400 && error.data?.error) {
      const errorMsg = error.data.error;
      
      // Handle specific backend error messages
      if (errorMsg.includes("expired")) {
        toast.error("Your password reset link has expired. Please request a new one.", {
          id: toastId,
          duration: 5000,
        });
      } else if (errorMsg.includes("already been used") || errorMsg.includes("Invalid token")) {
        toast.error("This password reset link has already been used. Please request a new one.", {
          id: toastId,
          duration: 5000,
        });
      } else if (errorMsg.includes("Token is required")) {
        toast.error("Password reset token is missing. Please request a new reset link.", {
          id: toastId,
          duration: 5000,
        });
      } else {
        toast.error(errorMsg, {
          id: toastId,
          duration: 5000,
        });
      }
    } else if (error?.name === "SyntaxError") {
      toast.error(
        "We encountered a technical issue. Please clear your browser cookies and try again. If the issue persists, reach out to support for assistance.",
        { id: toastId, duration: 5000 }
      );
    } else if (error?.status === "FETCH_ERROR") {
      toast.error(
        "Network Error: Unable to connect to the server. Please check your internet connection or try again later.",
        { id: toastId, duration: 5000 }
      );
    } else if (error?.status === 429) {
      toast.dismiss(toastId);
      setRateLimit({ open: true, message: rateLimitMessage(error) });
    } else {
      toast.error(
        "We couldn't process your request. Please check your information and try again.",
        {
          id: toastId,
          duration: 5000,
        }
      );
    }
  }
};


  const inputCls = "w-full pl-11 pr-11 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent focus:bg-white transition-all";

  return (
    <div className="flex min-h-[calc(100vh-80px)] bg-gradient-to-br from-violet-50 via-white to-slate-50">
      {showEmailVerifyNotification && (
        <EmailVerify
          onClose={handleEmailNotifiedClose}
          message={
            " Your password has been successfully reset. Now, sign in with your new password."
          }
        />
      )}

      <RateLimitModal
        open={rateLimit.open}
        message={rateLimit.message}
        title="Too many attempts"
        onClose={() => setRateLimit({ open: false, message: "" })}
      />

      {/* Left Pane — premium dark panel */}
      <div className="hidden lg:flex flex-col justify-center w-1/2 p-12 xl:p-16 relative overflow-hidden bg-[#160A2B]">
        {/* layered ambient glows */}
        <div className="absolute -top-32 -left-20 w-[28rem] h-[28rem] rounded-full bg-[#8B1FD9]/40 blur-[120px]" />
        <div className="absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-[#C026D3]/30 blur-[120px]" />
        <div className="absolute -bottom-40 left-1/4 w-[26rem] h-[26rem] rounded-full bg-indigo-600/20 blur-[120px]" />
        {/* subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.45) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

        <div className="relative z-10 max-w-md mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-white/15 bg-white/5 backdrop-blur text-[11px] font-semibold uppercase tracking-widest text-violet-200">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Almost There
          </span>
          <h2 className="text-4xl font-bold leading-[1.15] text-white mb-4">
            Choose a <br />
            <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent">new password.</span>
          </h2>
          <p className="text-white/60 text-[15px] leading-relaxed mb-10">
            Pick a strong password you don&apos;t use anywhere else. You&apos;ll use
            it to sign in to your UniEdu account from now on.
          </p>

          <ul className="space-y-4">
            {[
              { t: "At least 8 characters", d: "Longer is stronger." },
              { t: "Mix it up", d: "Use letters, numbers & symbols." },
              { t: "Keep it private", d: "Never reuse an old password." },
            ].map((s, i) => (
              <li key={i} className="flex items-start gap-3.5">
                <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-white/10 border border-white/15 backdrop-blur flex items-center justify-center text-violet-200">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <div>
                  <p className="text-sm font-semibold text-white/90">{s.t}</p>
                  <p className="text-xs text-white/45">{s.d}</p>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-12 text-xs text-white/40">© {new Date().getFullYear()} UniEdu — Your gateway to global education.</p>
        </div>
      </div>

      {/* Right Pane — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 sm:p-10">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Reset Your Password</h1>
            <p className="text-sm text-slate-400 mb-8">
              Enter and confirm your new password below.
            </p>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    {...register("password", { required: true })}
                    name="password"
                    placeholder="Enter your new password"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <LuEye /> : <LuEyeOff />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs mt-1.5 text-red-500 font-medium">This field is required.</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </span>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    {...register("confirmPassword", { required: true })}
                    name="confirmPassword"
                    placeholder="Re-enter your new password"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <LuEye /> : <LuEyeOff />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs mt-1.5 text-red-500 font-medium">This field is required.</p>
                )}
              </div>

              <button
                aria-label="Reset Password"
                type="submit"
                className="w-full text-white font-semibold py-3 rounded-xl shadow-lg shadow-violet-200 hover:opacity-90 transition-all outline-none"
                style={{ background: "linear-gradient(135deg,#8B1FD9,#C026D3)" }}
              >
                Reset Password
              </button>
            </form>

            <Link
              to="/sign-in"
              className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-btnColor-400 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
              </svg>
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPasswordConfirm;
